import React, { useState } from "react";
import SkillSheetBuilderModal from "../../../components/SkillSheetBuilderModal";
import { addStandardCourseItem, deleteStandardCourseItem, updateStandardCourseItem } from "../../../api/backend";
import { Notice } from "../ui/AdminUi";
import { I } from "../ui/icons";
import { EmptyNote, PanelHeader, SessionSelect, errorText } from "./common";

const ROW = { display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", borderBottom: "1px solid var(--ga-line)", flexWrap: "wrap" };
const ICON = { width: 34, height: 34, borderRadius: 8, display: "grid", placeItems: "center", background: "var(--ga-soft)", color: "var(--ga-sub)", flexShrink: 0 };

/** Skill sheets of a standard course, built and edited with the same builder as every other skill sheet. */
export default function SkillSheetsPanel({ draft, courseId, onChanged }) {
  const [builder, setBuilder] = useState(null);
  const [error, setError] = useState(null);
  const sheets = draft.skillSheets;

  const attempt = async (action, fallback) => {
    setError(null);
    try {
      const result = await action();
      onChanged();
      return result;
    } catch (e) {
      setError(errorText(e, fallback));
      throw e;
    }
  };

  // An auto-save may already have created this sheet; its id comes back in savedBankId, so it is updated, not copied.
  const save = ({ autosave, savedBankId, ...payload }) => {
    const id = builder.id ?? savedBankId ?? null;
    return attempt(
      () => (id
        ? updateStandardCourseItem(courseId, "skill-sheets", id, payload)
        : addStandardCourseItem(courseId, "skill-sheets", payload)),
      "The skill sheet could not be saved.");
  };

  return (
    <div className="panel" data-testid="skill-sheets-panel">
      <PanelHeader title="Skill sheets" subtitle="Evaluated by instructors at every training center that runs this course."
        action={<button type="button" className="b pri sm" onClick={() => setBuilder({})}><I.plus s={12} />Add skill sheet</button>} />
      <div className="panel-b" style={{ padding: 0 }}>
        {error && (
          <div style={{ padding: "12px 18px 0" }}>
            <Notice kind="bad">
              {error}{" "}
              <button type="button" className="ib" aria-label="Close" style={{ width: 24, height: 24, verticalAlign: "middle" }} onClick={() => setError(null)}><I.x s={14} /></button>
            </Notice>
          </div>
        )}
        {sheets.length === 0 ? <EmptyNote>No skill sheets yet.</EmptyNote> : sheets.map((s) => (
          <div key={s.id} data-testid={`skill-sheet-${s.id}`} style={ROW}>
            <span style={ICON}><I.shield s={16} /></span>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontWeight: 700 }}>{s.title}</div>
              <div className="sub" style={{ fontSize: 12.5 }}>{(s.items || []).length} steps{s.passingScore ? ` · pass at ${s.passingScore}` : ""}</div>
            </div>
            <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer", fontSize: 13 }}>
              <input type="checkbox" checked={s.requiredForPass}
                onChange={(e) => attempt(() => updateStandardCourseItem(courseId, "skill-sheets", s.id, { requiredForPass: e.target.checked }), "The skill sheet could not be changed.").catch(() => {})} />
              Required to pass
            </label>
            <SessionSelect sessions={draft.sessions} value={s.sessionId}
              onChange={(sessionId) => attempt(() => updateStandardCourseItem(courseId, "skill-sheets", s.id, { sessionId }), "The skill sheet could not be moved.").catch(() => {})} />
            <button type="button" className="b sec sm" onClick={() => setBuilder(s)}><I.pencil s={12} />Edit</button>
            <button type="button" className="ib" aria-label={`Remove ${s.title}`}
              onClick={() => attempt(() => deleteStandardCourseItem(courseId, "skill-sheets", s.id), "The skill sheet could not be removed.").catch(() => {})}><I.x s={16} /></button>
          </div>
        ))}
      </div>
      {builder && (
        <SkillSheetBuilderModal open initial={builder.id ? builder : undefined} onClose={() => setBuilder(null)} onSave={save} />
      )}
    </div>
  );
}
